import React from "react"
import hash from "object-hash"

import { useAuth } from "store/auth"

// configs
import config from "config"

// firebase
import {
  collection,
  query,
  doc,
  where,
  onSnapshot,
  updateDoc,
  orderBy,
  limit,
} from "firebase/firestore"
import { Firestore } from "services/firebase"
import { calculateChoicesCosts } from "utils/menu"

const OrderContext = React.createContext()

function useOrders() {
  const context = React.useContext(OrderContext)
  if (!context) {
    throw new Error(`useOrders must be used within an OrderProvider`)
  }

  const [orders] = context.orders
  const [order, setOrder] = context.order
  const [status] = context.status

  const vat = React.useMemo(() => {
    return order.reduce(
      (sum, item) =>
        sum +
        ((item.preis + calculateChoicesCosts(item.choices, item.settings)) *
          ((100 - item.pick_up_discount) / 100) *
          (item.tax || 0)) /
          (100 + (item.tax || 0)),
      0
    )
  }, [order])

  const mainVat = React.useMemo(() => {
    return order.reduce(
      (sum, item) =>
        sum +
        ((item.preis + calculateChoicesCosts(item.choices, item.settings)) *
          ((100 - item.pick_up_discount) / 100) *
          (item.mainTax || 0)) /
          (100 + (item.mainTax || 0)),
      0
    )
  }, [order])

  const pickupDiscounts = React.useMemo(() => {
    return order.reduce(
      (sum, item) => sum + (item.preis * (item.pick_up_discount || 0)) / 100,
      0
    )
  }, [order])

  const total = React.useMemo(() => {
    return order.reduce(
      (sum, item) =>
        sum + item.preis + calculateChoicesCosts(item.choices, item.settings),
      0
    )
  }, [order])

  return {
    status,
    order: {
      total,
      vat,
      mainVat,
      pickupDiscounts,
      add: (item) => {
        setOrder((prev) => [...prev, item])
      },
      remove: (itemId) => {
        const removeItemId = order.findIndex((item) => item.id === itemId)
        setOrder((prev) => [
          ...prev.filter((item, index) => index !== removeItemId),
        ])
      },
      list: order.map((item) => {
        const id = {
          id: item.id,
          choices: item.choices,
        }

        const hashedId = hash(id)

        return {
          ...item,
          hash: hashedId,
        }
      }),
      clear: () => {
        setOrder([])
      },
    },
    orders,
    currentOrders: orders.filter((order) => !order.completed),
  }
}

function OrderProvider(props) {
  const { isAuthentificated, user } = useAuth()

  const [orderValue, setOrderValue] = React.useState([])
  const [ordersValue, setOrdersValue] = React.useState([])
  const [statusValue, setStatusValue] = React.useState("loading")

  const currentCartInit = React.useRef(false)

  const order = React.useMemo(() => [orderValue, setOrderValue], [orderValue])
  const orders = React.useMemo(
    () => [ordersValue, setOrdersValue],
    [ordersValue]
  )
  const status = React.useMemo(
    () => [statusValue, setStatusValue],
    [statusValue]
  )

  const subscription = React.useRef()

  React.useEffect(() => {
    if (!isAuthentificated || !user || orderValue.length !== 0) return

    if (currentCartInit.current) return
    currentCartInit.current = true

    if (
      !user?.data?.cart?.[config.key] ||
      user.data.cart[config.key].length === 0
    )
      return

    console.log("✅ Loading cart")

    setOrderValue(user.data.cart[config.key])
  }, [orderValue, isAuthentificated, user])

  React.useEffect(() => {
    if (!isAuthentificated) {
      return
    }

    subscription.current = onSnapshot(
      query(
        collection(Firestore, "orders"),
        where("resto_key", "==", config.key),
        where(`creator_uid`, "==", user.uid),
        orderBy("created_at", "desc"),
        limit(100)
      ),
      async (querySnapshot) => {
        const data = []

        querySnapshot.forEach((doc) => {
          const item = {
            id: doc.id,
            ...doc.data({ serverTimestamps: "estimate" }),
          }

          data.push(item)
        })

        setOrdersValue(data)
        setStatusValue("ready")
      }
    )

    return () => subscription.current()
  }, [isAuthentificated, user])

  React.useEffect(() => {
    if (!isAuthentificated || !user) return

    updateDoc(doc(Firestore, "users", user.uid), {
      cart: {
        [config.key]: orderValue,
      },
    })
  }, [orderValue, isAuthentificated, user])

  return <OrderContext.Provider value={{ order, orders, status }} {...props} />
}

export { OrderProvider, useOrders }
